import React, { useState, useEffect } from "react";
import {
  X,
  AlertCircle,
  PlusCircle,
  ArrowDown,
  ChevronDown,
  CalendarDays,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { BASE_URL } from "utils/constant";

const UpdateStockModal = ({ isOpen, onClose, taskId, onSuccess }) => {
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [updateDate, setUpdateDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [comments, setComments] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAddMode, setIsAddMode] = useState(true);
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchMaterials();
    }
  }, [isOpen, taskId]);

  const fetchMaterials = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/task/get-task-materials/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ task_id: taskId }),
      });
      const data = await response.json();
      if (data.status) {
        setMaterials(data.data);
      }
    } catch (err) {
      setError("Failed to fetch materials");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedMaterial) {
      setError("Please select a material");
      return;
    }

    const numQuantity = parseFloat(quantity);
    const availableQuantity =
      selectedMaterial.estimated_quantity - selectedMaterial.total_used;

    if (numQuantity <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    if (!isAddMode && numQuantity > availableQuantity) {
      setError(
        `Cannot use more than available quantity (${availableQuantity})`
      );
      return;
    }

    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/inventory/create-stock/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          update_date: updateDate,
          entered_quantity: isAddMode ? numQuantity : -numQuantity,
          comment: comments,
          material: selectedMaterial.material_id,
          task_id: taskId,
        }),
      });

      const data = await response.json();
      if (data.status) {
        onSuccess?.();
        onClose();
      } else {
        setError(data.message || "Failed to update stock");
      }
    } catch (err) {
      setError("An error occurred while updating stock");
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
    setShowMaterialModal(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div
          className="bg-white rounded-lg w-[50vw] h-[75vh] "
          style={{
            overflowY: "auto",
          }}
        >
          <div className="flex justify-between px-6 pt-4 pb-2 items-center mb-4 border-b-[1px] border-[#00000045]">
            <div></div>
            <h2 className="text-[22px] font-semibold">Stock Update</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2 px-8 pb-2 border-b-[1px] border-[#00000045]">
              <label className="block text-[18px] font-medium text-[#232323]">
                Materials
              </label>
              <div
                className="w-full p-2 px-3  rounded-lg flex justify-between items-center cursor-pointer bg-[#F3F4F6]"
                onClick={() => setShowMaterialModal(true)}
              >
                {selectedMaterial
                  ? `${selectedMaterial.material_name} - ${selectedMaterial.specification}`
                  : "Select Material"}
                <ChevronDown className="h-5 w-5" />
              </div>
              {selectedMaterial && (
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-col items-start">
                    <p className="text-base font-medium text-[#232323]">
                      {selectedMaterial?.material_name}
                    </p>
                    <p className="text-sm text-[#737373]">
                      Sub Category
                      <p className="text-black">
                        {selectedMaterial?.subcategory}
                      </p>
                    </p>
                  </div>
                  <div className="flex flex-col items-start">
                    <p className="text-sm font-normal text-[#737373]">
                      Specification
                    </p>
                    <p className="text-sm text-black">
                      {selectedMaterial?.specification}
                    </p>
                  </div>
                  <div className="flex flex-col items-start">
                    <p className="text-sm font-normal text-[#737373]">
                      In Stock Amount
                    </p>
                    <p className="text-sm text-black">
                      {selectedMaterial?.estimated_quantity -
                        selectedMaterial?.total_used}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2 px-8 border-b-[1px] border-[#00000045] pb-2">
              <label className="block text-sm font-medium text-gray-700">
                Update Date
              </label>
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-start">
                  <p className="text-base font-semibold text-black">
                    {new Date(updateDate).toLocaleDateString("en-US", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <p className="text-sm text-[#737373]">
                    {new Date(updateDate).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="updateDate"
                    onClick={() => {
                      document.getElementById("updateDate").click();
                    }}
                    className="text-[#737373] flex flex-row items-center gap-2 hover:text-gray-700 px-2 py-1 rounded-2xl bg-[#E3E3E3] cursor-pointer"
                  >
                    Change
                    <CalendarDays className="h-5 w-5 text-[#737373]" />
                  </label>
                  <DatePicker
                    id="updateDate"
                    selected={new Date(updateDate)}
                    onChange={(date) =>
                      setUpdateDate(date.toISOString().split("T")[0])
                    }
                    maxDate={new Date()}
                    className="px-2 py-1 hidden rounded-md border text-sm"
                    calendarClassName="rounded-md"
                    dateFormat="yyyy-MM-dd"
                  />
                  {/* <CalendarDays className="h-5 w-5 text-[#737373]" /> */}
                </div>
              </div>
            </div>

            <div className="space-y-2 px-8">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Enter Amount
                </label>
                <div>
                  {/* <button
                    type="button"
                    className={`mr-2 px-3 py-1 rounded ${
                      isAddMode
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setIsAddMode(true)}
                  >
                    Added
                  </button> */}
                  <button
                    type="button"
                    className={`px-3 py-1 rounded ${
                      !isAddMode
                        ? "bg-red-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setIsAddMode(false)}
                  >
                    Used
                  </button>
                </div>
              </div>
              <div className="relative">
                <input
                  type="number"
                  className="w-full py-2 px-4 border rounded-md"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
                {selectedMaterial && !isAddMode && (
                  <p className="text-sm text-gray-500 mt-1">
                    Available:{" "}
                    {selectedMaterial.estimated_quantity -
                      selectedMaterial.total_used}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2 px-8">
              <label className="block text-sm font-medium text-gray-700">
                Remarks/Comments
              </label>
              <textarea
                className="w-full p-2 border rounded-md"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Type"
                rows={3}
              />
            </div>
            <div className="flex justify-center items-center mb-4">
              <button
                type="submit"
                className="w-[70%] bg-[#3B0069] text-white py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
                disabled={loading}
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>

      {showMaterialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Select Material</h2>
              <button
                onClick={() => setShowMaterialModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <input
                type="text"
                placeholder="Search Your Material"
                className="w-full p-2 border rounded-md"
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {materials.map((material) => (
                <div
                  key={material.id}
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleMaterialSelect(material)}
                >
                  <input
                    type="radio"
                    checked={selectedMaterial?.id === material.id}
                    readOnly
                    className="mr-2"
                  />
                  <div>
                    <p className="font-medium">{material.material_name}</p>
                    <p className="text-sm text-gray-500">
                      {material.specification}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateStockModal;
